import { useState } from 'react';
import { Card, Badge, Modal, FormGroup, FormInput, FormSelect, toast } from '@/components';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import styles from './Users.module.css';

interface ConsoleUser {
  id: number;
  name: string;
  email: string;
  role: string;
  created_at: string;
}

const roleLabel: Record<string, string> = { super_admin: 'Super Admin', admin: 'Admin', viewer: 'Viewer' };
const roleBadge: Record<string, 'purple' | 'blue' | 'gray'> = { super_admin: 'purple', admin: 'blue', viewer: 'gray' };
const roleOptions = [
  { value: 'admin', label: 'Admin — 日常運用（テナント管理・課金・サポート）' },
  { value: 'viewer', label: 'Viewer — 閲覧のみ' },
];

export function Users() {
  const { data: users, loading, refetch } = useApi<ConsoleUser[]>('/console/users');
  const [showAdd, setShowAdd] = useState(false);
  const [editUser, setEditUser] = useState<ConsoleUser | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ConsoleUser | null>(null);
  const [form, setForm] = useState({ name: '', email: '', role: 'admin', password: '' });
  const [saving, setSaving] = useState(false);

  const openAdd = () => {
    setForm({ name: '', email: '', role: 'admin', password: '' });
    setShowAdd(true);
  };

  const openEdit = (u: ConsoleUser) => {
    setForm({ name: u.name, email: u.email, role: u.role, password: '' });
    setEditUser(u);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast('名前とメールアドレスは必須です', 'error');
      return;
    }
    setSaving(true);
    try {
      if (editUser) {
        await api(`/console/users/${editUser.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ name: form.name, email: form.email, role: form.role }),
        });
        toast('ユーザーを更新しました', 'success');
        setEditUser(null);
      } else {
        await api('/console/users', {
          method: 'POST',
          body: JSON.stringify({ name: form.name, email: form.email, role: form.role, password: form.password || 'demo' }),
        });
        toast('ユーザーを追加しました', 'success');
        setShowAdd(false);
      }
      refetch();
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api(`/console/users/${deleteTarget.id}`, { method: 'DELETE' });
      toast('ユーザーを削除しました', 'success');
      setDeleteTarget(null);
      refetch();
    } catch (e) {
      toast(e instanceof Error ? e.message : '削除に失敗しました', 'error');
    }
  };

  if (loading) return <div className={styles.page}>読み込み中...</div>;

  return (
    <div className={styles.page}>
      <Card title="コンソールユーザー一覧" actions={
        <button className={styles.btnPrimary} onClick={openAdd}>+ ユーザー追加</button>
      }>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>名前</th>
              <th className={styles.th}>メールアドレス</th>
              <th className={styles.th}>ロール</th>
              <th className={styles.th}>作成日</th>
              <th className={styles.th}>操作</th>
            </tr>
          </thead>
          <tbody>
            {(users ?? []).map(u => (
              <tr key={u.id}>
                <td className={styles.td} style={{ fontWeight: 600 }}>{u.name}</td>
                <td className={styles.td} style={{ fontFamily: 'var(--mono)', fontSize: '11.5px' }}>{u.email}</td>
                <td className={styles.td}>
                  <Badge variant={roleBadge[u.role] ?? 'gray'}>{roleLabel[u.role] ?? u.role}</Badge>
                </td>
                <td className={styles.td} style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--ink3)' }}>
                  {new Date(u.created_at).toLocaleDateString('ja-JP')}
                </td>
                <td className={styles.td}>
                  <div className={styles.actions}>
                    <button className={styles.btnSm} onClick={() => openEdit(u)}>編集</button>
                    {u.role !== 'super_admin' && (
                      <button className={styles.btnDanger} onClick={() => setDeleteTarget(u)}>削除</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className={styles.roleDesc}>
          <div className={styles.roleDescItem}><span className={styles.roleDescLabel}>Super Admin:</span> 全権限（SSL更新・ユーザー管理・マスタ設定・テナント作成/削除）</div>
          <div className={styles.roleDescItem}><span className={styles.roleDescLabel}>Admin:</span> 日常運用（テナント管理・課金・サポート・リード管理）</div>
          <div className={styles.roleDescItem}><span className={styles.roleDescLabel}>Viewer:</span> 閲覧のみ（ダッシュボード・モニタリング・一覧表示）</div>
        </div>
      </Card>

      {/* 追加/編集モーダル */}
      <Modal
        open={showAdd || !!editUser}
        title={editUser ? 'ユーザー編集' : 'ユーザー追加'}
        onClose={() => { setShowAdd(false); setEditUser(null); }}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className={styles.btnSm} onClick={() => { setShowAdd(false); setEditUser(null); }}>キャンセル</button>
            <button className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
              {saving ? '保存中...' : editUser ? '更新' : '追加'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <FormGroup label="名前">
            <FormInput value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="山田 太郎" />
          </FormGroup>
          <FormGroup label="メールアドレス">
            <FormInput type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="user@jiayin.jp" />
          </FormGroup>
          <FormGroup label="ロール">
            <FormSelect value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
              {editUser?.role === 'super_admin' && <option value="super_admin">Super Admin</option>}
              {roleOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </FormSelect>
          </FormGroup>
          {!editUser && (
            <FormGroup label="初期パスワード" hint="空の場合は 'demo' が設定されます">
              <FormInput type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="初期パスワード" />
            </FormGroup>
          )}
        </div>
      </Modal>

      {/* 削除確認 */}
      <Modal
        open={!!deleteTarget}
        title="ユーザー削除"
        onClose={() => setDeleteTarget(null)}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className={styles.btnSm} onClick={() => setDeleteTarget(null)}>キャンセル</button>
            <button className={styles.btnDanger} onClick={handleDelete}>削除する</button>
          </div>
        }
      >
        <p style={{ fontSize: 13, lineHeight: 1.6 }}>
          <strong>{deleteTarget?.name}</strong>（{deleteTarget?.email}）を削除しますか？この操作は取り消せません。
        </p>
      </Modal>
    </div>
  );
}
